// React and service Components
import partCodesService from "services/part-code-service";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// MUI & Material Dashboard components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from '@mui/icons-material/Download';
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";

// E4A Components
import FileCard from "e4acomponents/Cards/FileCards";
import DataTable from "e4acomponents/Tables/DataTable";

// File icons
import pdfThumbnail from "assets/images/thumbnails/pdf.png";
import docxThumbnail from "assets/images/thumbnails/docx.png";
import xlsThumbnail from "assets/images/thumbnails/xls.png";
import pptxThumbnail from "assets/images/thumbnails/pptx.png";
import stepThumbnail from "assets/images/thumbnails/step.png";



function AttachmentsTab() {
    const [tableData, setTableData] = useState([]);
    const { id } = useParams();
    const [isVisible, setIsVisible] = useState(false);
    const [hasFiles, setHasFiles] = useState(false);

    const fileIcons = {
        'pdf': pdfThumbnail,
        'doc': docxThumbnail,
        'docx': docxThumbnail,
        'xls': xlsThumbnail,
        'xlsx': xlsThumbnail,
        'ppt': pptxThumbnail,
        'pptx': pptxThumbnail,
    };

    useEffect(() => {
        (async () => {
            console.log("fetching part by id: ", id);
            const response = await partCodesService.getPartById(id);
            if (!response.data.attributes.filePaths) {
                return;
            }
            console.log(response.data.attributes.filePaths);
            setTableData(getRows(response.data.attributes.filePaths));
            setHasFiles(response.data.attributes.has_files);
            setIsVisible(true);
        })()
    }, []);

    // Helper function to select the correct icon for a file
    const selectIcon = (filename) => {
        let ext = filename.split('.').pop();
        if (fileIcons[ext]) {
            return fileIcons[ext];
        }
        return stepThumbnail;
    }

    // Generates the rows for the DataTable
    const getRows = (info) => {
        let updatedInfo = Object.entries(info)
            .map(([key, value], index) => {
                return {
                    index: index + 1,
                    filename: key,
                    filekey: value,
                };
            });
        return updatedInfo;
    }

    // DataTable containing the files and action on files
    const dataTableData = {
        columns: [
            { Header: "#", accessor: "index", width: "10%" },
            {
                Header: "Type",
                disableSortBy: true,
                accessor: "type",
                width: "15%",
                Cell: (row) => {
                    return (
                        <MDBox>
                            <MDAvatar src={selectIcon(row.row.original.filename)} alt="file icon" size="xs" variant="rounded" />
                        </MDBox>
                    )
                },
            },
            { Header: "File Name", accessor: "filename", width: "55%" },
            {
                Header: "Actions",
                disableSortBy: true,
                accessor: "actions",
                width: "20%",
                Cell: (row) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            <Tooltip
                                title="Download File"
                            >
                                <IconButton onClick={generateDownloadLink(row.row.original.filekey, row.row.original.filename)} >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </MDBox>
                    )
                }
            }
        ],
        rows: tableData
    }

    // Generates a download link for a file
    const generateDownloadLink = (filekey, filename) => {
        return async () => {
            try {
                // Fetch a pre-signed URL for the file
                const response = await partCodesService.getFile(filekey);
                const url = response.url;
                console.log("Pre-signed URL:", url); // Log the pre-signed URL

                // Creates a link element inside the Download Icon, clicking it will download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error("Error generating download link:", error);
            }
        };
    };

    return (
        <MDBox pt={6} pb={3}>

            {hasFiles ?
                (<Card
                    sx={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                        transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
                    }}
                >
                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
                        <MDTypography variant="h5" fontWeight="medium">
                            Attached Files
                        </MDTypography>
                    </MDBox>
                    <MDBox
                        sx={{
                            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            opacity: tableData ? 1 : 0,
                            visibility: tableData ? 'visible' : 'hidden',
                            maxHeight: tableData ? '2000px' : '0px',
                            marginTop: tableData ? '16px' : '0px',
                            overflow: 'hidden',
                            '& .MuiTableRow-root': {
                                opacity: 0,
                                animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                                animationDelay: 'calc(var(--row-index) * 50ms)'
                            },
                            '@keyframes fadeIn': {
                                '0%': {
                                    opacity: 0,
                                    transform: 'translateY(10px)'
                                },
                                '100%': {
                                    opacity: 1,
                                    transform: 'translateY(0)'
                                }
                            }
                        }}
                    >
                        {tableData &&
                            (
                                <DataTable table={dataTableData} canSearch tableId="attachments-tab-table" />
                            )
                        }
                    </MDBox>
                </Card>
                ) : (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="100%" p={5} mt={5} mb={5}>
                        <MDTypography variant="h5" fontWeight="medium" p={5} mt={5} mb={5}>
                            No files attached to this part code.
                        </MDTypography>
                    </MDBox>
                )
            }
        </MDBox>
    )
}
export default AttachmentsTab;